class BatterySpecsSection {
  constructor(section) {
    this.section = section;
    this.animated = false;
    this.imageAnimated = false;
    this.gsapRetryCount = 0;
    this.cacheElements();
    this.init();
  }

  cacheElements() {
    this.numberElements = this.section.querySelectorAll('.battery-specs-section__spec-number');
    this.imageElement = this.section.querySelector('[data-animate="image"]');
    this.titleElements = this.section.querySelectorAll('.battery-specs-section__title');
    this.specsContainer = this.section.querySelector('.battery-specs-section__specs');
  }

  init() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.setupOdometerNumbers();
        this.setupIntersectionObserver();
      });
      requestIdleCallback(() => {
        this.setupGsapTitleAnimation();
      }, { timeout: 100 });
    } else {
      this.setupOdometerNumbers();
      this.setupIntersectionObserver();
      this.setupGsapTitleAnimation();
    }
  }

  setupOdometerNumbers() {
    this.numberElements.forEach(element => {
      const target = element.dataset.target;
      if (!target || target === '' || target === '0' || parseInt(target) === 0) return;
      const digits = target.toString().split('');
      element.textContent = '';
      digits.forEach((digit, index) => {
        const wrapper = document.createElement('span');
        wrapper.className = 'battery-specs-section__digit-wrapper';
        const column = document.createElement('span');
        column.className = 'battery-specs-section__digit-column';
        column.dataset.target = digit;
        column.dataset.index = index;
        const targetDigit = parseInt(digit);
        const totalCycles = 3;
        const maxDigit = (totalCycles * 10) + targetDigit;
        for (let i = 0; i <= maxDigit; i++) {
          const digitSpan = document.createElement('span');
          digitSpan.className = 'battery-specs-section__digit';
          digitSpan.textContent = i % 10;
          column.appendChild(digitSpan);
        }
        wrapper.appendChild(column);
        element.appendChild(wrapper);
      });
    });
  }

  setupIntersectionObserver() {
    const observerOptions = { root: null, rootMargin: '0px', threshold: 0.3 };

    this.numberObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.animated) {
          this.animated = true;
          this.animateNumbers();
        }
      });
    }, observerOptions);

    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.imageAnimated) {
          this.imageAnimated = true;
          this.animateImage();
        }
      });
    }, { ...observerOptions, threshold: 0.2 });

    if (this.specsContainer) this.numberObserver.observe(this.specsContainer);
    if (this.imageElement) this.imageObserver.observe(this.imageElement);
  }

  animateNumbers() {
    this.numberElements.forEach(element => {
      const target = element.dataset.target;
      if (!target || target === '' || target === '0' || parseInt(target) === 0) return;
      element.querySelectorAll('.battery-specs-section__digit-column').forEach(column => {
        this.animateDigitColumn(column, parseInt(column.dataset.target));
      });
    });
  }

  animateDigitColumn(column, targetDigit) {
    const digitHeight = column.querySelector('.battery-specs-section__digit').offsetHeight;
    const totalCycles = 3;
    const translateY = -((totalCycles * 10 + targetDigit) * digitHeight);
    column.classList.add('animating');
    column.style.transform = `translate3d(0, ${translateY}px, 0)`;
    const onComplete = () => {
      column.classList.remove('animating');
      column.removeEventListener('transitionend', onComplete);
    };
    column.addEventListener('transitionend', onComplete);
  }

  animateImage() {
    const images = this.imageElement?.querySelectorAll('.battery-specs-section__image') || [];
    images.forEach(image => {
      setTimeout(() => image.classList.add('is-visible'), 100);
    });
  }

  setupGsapTitleAnimation() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initGsapTitles(), { once: true });
    } else {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.initGsapTitles());
      } else {
        setTimeout(() => this.initGsapTitles(), 100);
      }
    }
  }

  initGsapTitles() {
    if (typeof gsap === 'undefined') {
      if (this.gsapRetryCount < 5) {
        this.gsapRetryCount++;
        setTimeout(() => this.initGsapTitles(), 500);
      }
      return;
    }
    this.titleElements.forEach((title, index) => {
      setTimeout(() => {
        this.setupSplitForTitle(title);
        this.setupTitleIntersectionObserver(title);
      }, index * 100);
    });
  }

  setupSplitForTitle(title) {
    if (title.dataset.splitReady === 'true') return;
    if (title.anim) {
      title.anim.progress(1).kill();
      if (title.split) title.split.revert();
    }
    if (typeof SplitText !== 'undefined') {
      title.split = SplitText.create(title, { type: "words,chars", linesClass: "split-line" });
    } else {
      this.splitTextManually(title);
    }
    const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
    if (!chars || chars.length === 0) return;

    if (typeof gsap !== 'undefined') gsap.set(chars, { y: 80, opacity: 0 });
    setTimeout(() => this.updateCharGradients(title), 50);
    title.dataset.splitReady = 'true';
    title.dataset.gsapReady = 'true';
    title.dataset.isAnimating = 'false';
  }

  splitTextManually(element) {
    if (element.querySelector('.char')) return;
    const text = element.innerHTML;
    const lines = text.split('<br>');
    element.innerHTML = '';
    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0) element.appendChild(document.createElement('br'));
      line.trim().split(/(\s+)/).forEach(word => {
        if (word.match(/\s/)) {
          element.appendChild(document.createTextNode(word));
          return;
        }
        word.split('').forEach(char => {
          const span = document.createElement('span');
          span.className = 'char';
          span.textContent = char;
          span.style.display = 'inline-block';
          element.appendChild(span);
        });
      });
    });
  }

  setupTitleIntersectionObserver(title) {
    title.dataset.hasTriggered = 'false';
    title.dataset.isVisible = 'false';
    let lastScrollY = window.scrollY || window.pageYOffset;

    const titleObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const currentScrollY = window.scrollY || window.pageYOffset;
        const isScrollingDown = currentScrollY > lastScrollY;
        lastScrollY = currentScrollY;

        const isVisibleEnough = entry.intersectionRatio >= 0.15;
        const hasTriggered = title.dataset.hasTriggered === 'true';

        if (entry.isIntersecting && isVisibleEnough) {
          title.dataset.isVisible = 'true';
          if (!hasTriggered && title.dataset.isAnimating !== 'true') {
            title.dataset.hasTriggered = 'true';
            if (isScrollingDown) {
              this.animateTitleChars(title);
            } else {
              const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
              if (chars && chars.length > 0 && typeof gsap !== 'undefined') {
                gsap.set(chars, { y: 0, opacity: 1 });
                this.updateCharGradients(title);
              }
            }
          }
        } else if (!entry.isIntersecting || entry.intersectionRatio < 0.05) {
          if (title.dataset.isVisible === 'true' && title.dataset.isAnimating !== 'true') {
            const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
            if (chars && chars.length > 0 && typeof gsap !== 'undefined') {
              gsap.set(chars, { y: 80, opacity: 0 });
            }
            title.dataset.hasTriggered = 'false';
            title.dataset.isVisible = 'false';
          }
        }
      });
    }, {
      root: null,
      rootMargin: '50px 0px 50px 0px',
      threshold: [0, 0.1, 0.15, 0.2, 0.25, 0.3, 0.4, 0.5]
    });

    titleObserver.observe(title);
    title.observer = titleObserver;

    requestAnimationFrame(() => {
      const rect = title.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (rect.top < windowHeight * 0.85 && rect.bottom > 0 && title.dataset.hasTriggered === 'false') {
        const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
        if (chars && chars.length > 0 && typeof gsap !== 'undefined') {
          gsap.set(chars, { y: 0, opacity: 1 });
          this.updateCharGradients(title);
          title.dataset.hasTriggered = 'true';
          title.dataset.isVisible = 'true';
        }
      }
    });
  }

  animateTitleChars(title) {
    if (!title.dataset.gsapReady || typeof gsap === 'undefined') return;
    const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
    if (!chars || chars.length === 0) return;
    if (title.anim) title.anim.progress(1).kill();

    title.dataset.isAnimating = 'true';
    title.anim = gsap.to(chars, {
      y: 0, opacity: 1, duration: 0.6, ease: 'circ.out', stagger: 0.02,
      onComplete: () => {
        this.updateCharGradients(title);
        title.dataset.isAnimating = 'false';
      }
    });
  }

  updateCharGradients(title) {
    const chars = title.split ? title.split.chars : title.querySelectorAll('.char');
    if (!chars || chars.length === 0) return;
    const gradient = 'linear-gradient(114deg, #0068FF 33.4%, #000 64.14%)';
    const titleRect = title.getBoundingClientRect();
    const fullWidth = titleRect.width;
    chars.forEach(char => {
      const charRect = char.getBoundingClientRect();
      const offsetX = charRect.left - titleRect.left;
      char.style.background = gradient;
      char.style.backgroundSize = `${fullWidth}px 100%`;
      char.style.backgroundPosition = `-${offsetX}px 0`;
      char.style.backgroundClip = 'text';
      char.style.webkitBackgroundClip = 'text';
      char.style.webkitTextFillColor = 'transparent';
    });
  }

  destroy() {
    if (this.numberObserver) this.numberObserver.disconnect();
    if (this.imageObserver) this.imageObserver.disconnect();
    this.titleElements.forEach(title => {
      if (title.observer) title.observer.disconnect();
      if (title.anim) title.anim.kill();
      if (title.split) title.split.revert();
    });
    this.section = null;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.battery-specs-section').forEach(section => {
    section.instance = new BatterySpecsSection(section);
  });
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.battery-specs-section');
    if (section) section.instance = new BatterySpecsSection(section);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.battery-specs-section');
    if (section && section.instance) {
      section.instance.destroy();
      section.instance = null;
    }
  });
}
